import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';

import AppStack, { type AppStackItem, type AppStackItemProps } from '@atlassian/app-stack';
import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';
import {
	getLogoForSourceType,
	getSourceType,
} from '@atlassian/rovo-platform-ui-components/source-logo-stack/source-util';

export const SUGGESTION_SOURCE_APP_STACK_TESTID = 'ai-suggestion-source-app-stack';

/**
 * Caps how many logos are shown. The design shows two; a third leaves room for a genuinely
 * multi-app suggestion without the stack crowding the row it shares with the Restricted
 * lozenge.
 */
const MAX_VISIBLE_APPS = 3;

/** The source types `source-util` knows how to map to a logo. */
type SourceType = ReturnType<typeof getSourceType>;

const getSuggestionSourceApps = (sources: Suggestion['sources']): AppStackItem[] => {
	const apps: AppStackItem[] = [];
	const seen = new Set<SourceType>();

	for (const { url } of sources ?? []) {
		if (!url) {
			continue;
		}

		const sourceType = getSourceType(url);

		if (sourceType === 'unknown' || seen.has(sourceType)) {
			continue;
		}

		seen.add(sourceType);
		apps.push({
			component: getLogoForSourceType(sourceType) as React.ComponentType<AppStackItemProps>,
		});
	}

	return apps;
};

export type SuggestionItemSourceAppStackProps = {
	sources: Suggestion['sources'];
};

export function SuggestionItemSourceAppStack({
	sources,
}: SuggestionItemSourceAppStackProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();
	const data = useMemo(() => getSuggestionSourceApps(sources), [sources]);

	if (data.length === 0) {
		return null;
	}

	return (
		<AppStack
			data={data}
			// `xsmall` is the design's 20px tile, and gives the -4px overlap it shows.
			size="xsmall"
			maxCount={MAX_VISIBLE_APPS}
			label={formatMessage(aiSuggestionsMessages.cardSourceAppsLabel)}
			testId={SUGGESTION_SOURCE_APP_STACK_TESTID}
		/>
	);
}
